import { z } from 'zod';
import { freeText, label, personName } from './text-policy.js';
import { FIELD_TEXT_MAX_LENGTH_CEILING, fieldTypeSchema } from './check-in-flow.js';
import { pageQuerySchema } from './http.js';
import { multiLanguageContentSchema } from './i18n.js';
import { notificationDeliveryDTOSchema } from './notification.js';
/**
 * Lifecycle of a walk-up visit.
 *
 * Must stay identical — same members, **same order** — to the `VisitStatus`
 * Prisma enum in packages/db/prisma/schema.prisma, proven by a lock-step test
 * in packages/db, exactly like `PERSON_TYPES` in ./person.ts.
 */
export const VISIT_STATUSES = ['CHECKED_IN', 'CHECKED_OUT'];
export const visitStatusSchema = z.enum(VISIT_STATUSES);
/**
 * The host's answer to an arrival DM (PRD-010 CHAT-08): the "On my way" / "Ask them to wait"
 * buttons in Slack and Teams. EXTENSIBLE — a further answer is one member here and one
 * `ALTER TYPE … ADD VALUE`. `RUNNING_LATE` (S48-lh45) is offered by InstaCheckin Express only;
 * the chat DM keeps two buttons.
 *
 * Must stay identical — same members, same order — to the `VisitorHostResponse` Prisma enum,
 * proven by `packages/db/src/visitor-host-response-enums.test.ts` (docs/adr/0011).
 */
export const VISIT_HOST_RESPONSES = ['ON_MY_WAY', 'ASK_TO_WAIT', 'RUNNING_LATE'];
export const visitHostResponseSchema = z.enum(VISIT_HOST_RESPONSES);
/** The LATEST answer and when it was given. A repeated click replaces it; the audit trail keeps
 *  every one (`visit.host_responded`). */
export const visitHostReplyDTOSchema = z.object({
    response: visitHostResponseSchema,
    respondedAt: z.string().datetime(),
});
/**
 * `GET /api/device/v1/visits/:id/host-reply` (S48-lh45) — what a kiosk shows after a check-in
 * ("Your host is coming down"). A NEW route, so no existing device body changes. Clients MUST
 * ignore an unknown `hostResponse.response` value: the set grows (adr/0009).
 */
export const deviceVisitHostReplyDTOSchema = z.object({
    visitId: z.string().min(1),
    status: visitStatusSchema,
    hostResponse: visitHostReplyDTOSchema.nullable(),
});
/**
 * The ONLY shape of a `Person` a kiosk device ever sees when searching for a
 * host to check a visitor in against.
 *
 * `personDTOSchema` (./person.ts) carries `email` and
 * `phone` because the directory's job is to hold them — but a kiosk is a
 * device principal sitting in a public lobby, not a signed-in employee
 * reading their own directory. Host search must never leak a colleague's
 * email or phone to that surface (PRD-004 CHK-13), so this DTO is a
 * deliberately separate, narrower allow-list, not a `.pick()` off
 * `personDTOSchema` that could accidentally widen later.
 */
export const hostSummaryDTOSchema = z.object({
    id: z.string().min(1),
    displayName: z.string().min(1),
    title: z.string().nullable(),
    departmentName: z.string().nullable(),
});
/**
 * One stored answer to a non-system `VisitorCheckInField` question, read back on the
 * visit (the write half of this loop is `checkInAnswerSchema` below; a follow-up slice
 * adds the `/admin/visits/:id` UI that renders these).
 *
 * `fieldKey`/`fieldType`/`label` are a SNAPSHOT taken at submit time — see
 * `VisitorVisitAnswer` in `schema.prisma` — not a live join to today's field library,
 * because a tenant may rename, retype or delete the field after the visit that
 * answered it, and "which question was this visitor actually asked" must survive all
 * three (the same FLOW-17 reasoning `VisitorVisit.flowId` documents).
 *
 * Lenient outbound, same asymmetry as `checkInFieldDTOSchema` in `./check-in-flow.js`:
 * unbounded and `.passthrough()`, so a stored row can never fail this read. `value` is
 * `z.unknown()` rather than the inbound `string | boolean` union for the same reason —
 * the shape that was actually validated at submit time is not this schema's business
 * to re-check on the way out.
 */
export const visitAnswerDTOSchema = z
    .object({
    fieldKey: z.string().min(1),
    fieldType: fieldTypeSchema,
    label: multiLanguageContentSchema,
    value: z.unknown(),
})
    .passthrough();
/**
 * The wire shape of a visit. An allow-list, deliberately narrower than the
 * row: `tenantId` and `deviceId` never leave the server (docs/008 S1/S7) —
 * `deviceId` is an internal audit detail (which kiosk recorded this visit),
 * not something an admin visitor log or a kiosk confirmation screen needs to
 * render.
 *
 * `host` is nullable: `hostPersonId` can be cleared if the host record is
 * later removed from the directory, and the visit must still render.
 *
 * `answers` is always present, even for a visit checked in before this field existed
 * or through a kiosk that submitted none — `[]`, never `undefined` (see
 * `toVisitDTO()`).
 */
export const visitDTOSchema = z.object({
    id: z.string().min(1),
    status: visitStatusSchema,
    checkedInAt: z.string().datetime(),
    checkedOutAt: z.string().datetime().nullable(),
    visitorName: z.string().min(1),
    visitorEmail: z.string().nullable(),
    visitorCompany: z.string().nullable(),
    purpose: z.string().nullable(),
    host: hostSummaryDTOSchema.nullable(),
    locationId: z.string().min(1),
    locationName: z.string().nullable(),
    /** The visitor type chosen on the kiosk (S24), or `null` when none was recorded. */
    visitorTypeId: z.string().min(1).nullable(),
    visitorTypeLabel: multiLanguageContentSchema.nullable(),
    /**
     * The invite this arrival redeemed, or `null` for a walk-up (INV-08).
     *
     * PUBLISHED DELIBERATELY, AND ONLY THE ID. It is a tenant-scoped opaque id the same
     * reader can already see on `/admin/invites`, so it leaks nothing the caller was not
     * already entitled to — and it is the whole basis of "invited, not walk-in" in the
     * visitor log. The invite's visitor name, host, window, note and expiry stay where they
     * are: a list of 20 visits must not become 20 joined invites, and `inviteDTOSchema` is
     * the shape that owns them.
     *
     * NULLABLE AND ALWAYS PRESENT — `null`, never `undefined` and never absent. Every visit
     * checked in before S11, and every walk-up after it, carries `null`, so a client can
     * branch on one predicate rather than on whether a key exists.
     */
    inviteId: z.string().nullable(),
    answers: z.array(visitAnswerDTOSchema),
});
/**
 * The photograph taken at check-in (PRD-004 CHK-21), as METADATA — appears on
 * `visitDetailDTOSchema` and nowhere else.
 *
 * DELIBERATELY ABSENT, each for its own reason:
 *   the bytes    — a photograph is a reference, never a blob (milestone Constraint 2,
 *                  CHK-23). No base64, no data URL, not now and not "just for the badge".
 *   `storageKey` — an internal path. A client that has one is a client that could build
 *                  one, which is what docs/008 S11 exists to prevent.
 *   any URL      — S13 serves the bytes from `GET /api/web/visits/:id/photo` behind the
 *                  SAME `visitor.visit.read` permission that returned this object
 *                  (S13-TASK-007). A URL in a DTO is a capability in a DTO; see the
 *                  milestone Technical Note "Why there is no signed URL in v1".
 *
 * `contentType` is a plain bounded string and NOT `visitorPhotoContentTypeSchema`, unlike
 * `uploadedFileDTOSchema` in ./storage.js. That enum is correct on the UPLOAD response,
 * where the value was validated microseconds earlier in the same request. Here the value
 * was validated on some other day under whatever allow-list was in force then, so binding
 * this read to today's enum would mean that narrowing `VISITOR_PHOTO_CONTENT_TYPES` later
 * turns every visit-detail read of an older photo into a failed parse — a 500 on a page
 * that should simply have shown the picture. A read contract outlives the write contract
 * that produced its rows.
 */
export const visitPhotoDTOSchema = z.object({
    /** The opaque `StoredFile.id`. The ONLY handle a client ever gets, and the id
     *  `GET /api/web/visits/:id/photo` resolves server-side to a key it never publishes. */
    id: z.string().min(1),
    contentType: z.string().min(1).max(128),
    byteSize: z.number().int().positive(),
    /** When the object was banked, which is when the kiosk captured it — not when the
     *  visitor checked in. The two differ by the seconds between the photo screen and the
     *  confirm button, and a support question about a mismatched face is answered by the
     *  former. */
    createdAt: z.string().datetime(),
});
/**
 * One accepted agreement as the admin visit detail reads it (PRD-008 DOC-04). Deliberately
 * denormalized and flat: `documentName` and `versionNumber` are copied at write time, so this
 * row still reads correctly after the document is renamed or a version 3 is published — which
 * is the entire point of the milestone.
 *
 * ADMIN-FACING ONLY. This schema is reachable from `visitDetailDTOSchema` and from nothing
 * else; `visitDTOSchema` — the body the DEVICE gets back from `POST /api/device/v1/visits` —
 * must never carry it (docs/008 S7). A visitor's typed name does not travel back to the tablet
 * the next person walks up to.
 */
export const visitAgreementAcceptanceDTOSchema = z.object({
    id: z.string().min(1),
    documentId: z.string().min(1),
    documentName: z.string().min(1),
    documentVersionId: z.string().min(1),
    versionNumber: z.number().int().positive(),
    /**
     * `null` for a drawn-signature-only acceptance (document-signing DOC-05, reversing S15
     * Constraint 2) — never a fabricated placeholder. Matches
     * `agreementAcceptanceInputSchema.typedName`'s optionality in spirit: the admin console
     * branches on `null` to show the signature instead of a typed name.
     */
    typedName: z.string().min(1).nullable(),
    /**
     * The `StoredFile.id` of a drawn/touch signature (document-signing DOC-05, reversing S15
     * Constraint 2), or `null` for a typed-name-only acceptance. Same "opaque id, never a key"
     * rule `visitPhotoDTOSchema.id` states — the admin console resolves it through
     * `GET /visits/:id/photo`'s sibling image route, never a raw storage path.
     */
    signatureFileId: z.string().min(1).nullable(),
    /**
     * The version's `title`/`body` WITH FIELD SUBSTITUTION APPLIED AND FROZEN at the moment of
     * acceptance — what this visitor actually read, which can differ from the version's own
     * current copy (document-signing "Field substitution"). `null` for every acceptance recorded
     * before this column existed.
     */
    renderedTitle: z.string().nullable(),
    /** See `renderedTitle` immediately above — same reasoning, same nullability. */
    renderedBody: z.string().nullable(),
    /** The locale `renderedTitle`/`renderedBody` were substituted in. `null` alongside them. */
    renderedLocale: z.string().nullable(),
    acceptedAt: z.string().datetime(),
});
/**
 * ONE visit, for `GET /api/web/visits/:id` and nothing else.
 *
 * WHY A SECOND SCHEMA RATHER THAN A FIELD ON `visitDTOSchema`. `VisitDTO` is the shape the
 * LIST returns and the shape `POST /api/device/v1/visits` returns. Putting `notificationDeliveries`
 * on it would make a 20-row visitor log do 20 extra queries for a column nothing renders, and
 * would change a device-API response body — which this milestone's Definition of Done forbids
 * outright. The detail read is the one caller that needs it, so the detail read is the one
 * shape that carries it.
 *
 * `.extend`, so every `VisitDetailDTO` is structurally a `VisitDTO` and any consumer typed
 * against the narrower one keeps working.
 */
export const visitDetailDTOSchema = visitDTOSchema.extend({
    /**
     * What was sent about this visit — NEWEST FIRST, and capped by the server at
     * `MAX_DELIVERIES_PER_SUBJECT` (20). Empty is normal, not an error: every visit checked
     * in before S12 has none, and a tenant whose host had no address on file has exactly one
     * `SKIPPED` row rather than zero.
     */
    notificationDeliveries: z.array(notificationDeliveryDTOSchema),
    /**
     * The photograph taken at check-in, or `null`.
     *
     * NULLABLE AND ALWAYS PRESENT — `null`, never `undefined` and never an absent key, the
     * same rule `inviteId` above states and for the same reason: a client branches on one
     * predicate, not on whether a key exists.
     *
     * `null` IS THE OVERWHELMING MAJORITY AND IS NOT AN ERROR. Every visit checked in before
     * S13, every walk-up through a flow with no `PHOTO_CAPTURE` screen, and every visitor who
     * declined a skippable one (CHK-24) carries `null`. A client rendering this must show an
     * honest "no photo" placeholder, never an error state and never a broken image.
     */
    photo: visitPhotoDTOSchema.nullable(),
    /** Every agreement accepted during this check-in, oldest first. `[]` for a visit that
     *  carried none — a walk-up through a flow with no agreement screen, or a kiosk build that
     *  predates S15. Never `null`, so the UI needs no null branch (S15-TASK-015). */
    agreementAcceptances: z.array(visitAgreementAcceptanceDTOSchema),
    /**
     * PRD-009 PRT-07. True once a print record for this visit has reached `PRINTED` —
     * never on enqueue, never on a failure, and never set back to false.
     *
     * ON THE DETAIL SCHEMA AND NOT ON `visitDTOSchema`, for the reason that schema's own
     * comment gives: `visitDTOSchema` is the body `POST /api/device/v1/visits` returns, and
     * this milestone's Definition of Done forbids changing a device-API response body. The
     * kiosk has no use for it either — it knows what it just printed.
     *
     * ALWAYS PRESENT, never `undefined`: every visit checked in before S35 reads `false`,
     * which is correct, so a client branches on one predicate rather than on key presence.
     */
    badgePrinted: z.boolean(),
    /**
     * PRD-010 CHAT-08 — the host's latest chat reply, or `null` (no reply, or no chat). On the
     * detail and list schemas only, never `visitDTOSchema`, for the device-API reason above.
     */
    hostResponse: visitHostReplyDTOSchema.nullable(),
});
/**
 * `VisitDTO` plus one cheap boolean, for `GET /api/web/visits` — the admin visitor log —
 * and nowhere else.
 *
 * WHY A THIRD SCHEMA RATHER THAN A FIELD ON `visitDTOSchema`, same reasoning
 * `visitDetailDTOSchema` states just above: `VisitDTO` is also the body of
 * `POST /api/device/v1/visits`, and that response shape must not change.
 *
 * WHY A BOOLEAN RATHER THAN `visitPhotoDTOSchema.nullable()` (what the detail read uses).
 * `photo` there is answered by joining `StoredFile` for `contentType`/`byteSize`/`createdAt` —
 * fine for one row, but exactly the "20 extra queries for a column nothing renders" cost
 * `visitDetailDTOSchema`'s comment warns against for a list. `hasPhoto` is answerable from
 * `VisitorVisit.photoFileId` alone — a column already on the row, not a join — so a 20-row
 * page costs nothing extra to carry it. The admin console uses it only to decide whether to
 * ask `GET /visits/:id/photo` for a row at all; it is never itself rendered.
 */
export const visitListItemDTOSchema = visitDTOSchema.extend({
    hasPhoto: z.boolean(),
    /** See `visitDetailDTOSchema.hostResponse` — the front desk reads it off the log. */
    hostResponse: visitHostReplyDTOSchema.nullable(),
});
/**
 * One answer to a non-system `VisitorCheckInField` question, submitted alongside a
 * check-in — the write half of PRD-003's configurable flows (`VisitorCheckInField.key`'s
 * own comment in `schema.prisma`: "the name this answer is stored under").
 *
 * `fieldKey` is `VisitorCheckInField.key`, NEVER a placement id. Two reasons:
 * `resolvedCheckInFieldSchema` in `./check-in-flow.js` explicitly withholds its
 * placement `id` from any submission contract ("the submission contract is not
 * defined yet, and `id` is not (yet) part of it") — this schema is that contract, and
 * it is built on the identifier that document says is the one carrying meaning across
 * flows and tenants; and a placement id would break the moment an admin re-adds a field
 * an override had removed, while `key` is immutable and unique per tenant for exactly
 * this purpose.
 *
 * `fieldKey`'s shape mirrors `createCheckInFieldSchema`'s `key` in `./check-in-flow.js`
 * — same regex, same bound — because it must be able to name any field that schema can
 * create.
 *
 * `fieldKey` must never name one of `SYSTEM_FIELD_KEYS` (`visitor_name`,
 * `visitor_email`, `visitor_company`, `host`, `purpose`): each already has a dedicated
 * typed field below via `SYSTEM_FIELD_VISIT_TARGET`, so an answer naming one has
 * nowhere to go and is refused by the server the same way an answer to a field the
 * resolved flow does not ask is (`service.ts#createVisit`) — this schema cannot check
 * that itself, since it does not know which keys a tenant's library holds.
 *
 * `value` is loose on purpose: `string | boolean` covers every `FIELD_TYPES` member
 * except `HOST_LOOKUP` (never answerable this way — a kiosk names its host through
 * `hostPersonId`, not an answer). Which shape a given `fieldKey` actually requires (a
 * DROPDOWN option, a bounded string, a `YYYY-MM-DD` date) can only be checked against
 * the flow resolved for the visit's own location, so that refinement happens
 * server-side, not in this schema.
 */
export const checkInAnswerSchema = z
    .object({
    fieldKey: z
        .string()
        .trim()
        .max(64)
        .regex(/^[a-z](?:[a-z0-9_]*[a-z0-9])?$/, { message: 'INVALID_FIELD_KEY' }),
    value: z.union([freeText({ max: FIELD_TEXT_MAX_LENGTH_CEILING }), z.boolean()]),
})
    .strict();
/**
 * A visit can carry at most this many acceptances. A check-in flow with more than ten
 * agreement screens is a configuration mistake, not a check-in; the cap exists so a
 * malformed or hostile body cannot make one submit write an unbounded number of rows.
 */
export const MAX_AGREEMENT_ACCEPTANCES_PER_VISIT = 10;
/**
 * The bound on the name a visitor types to accept. Generous enough for any real name plus
 * a title; short enough that the column is not a free-text sink.
 */
export const ACCEPTANCE_TYPED_NAME_MAX_CHARS = 200;
/**
 * One agreement a visitor accepted during this check-in (PRD-008 DOC-03/DOC-04).
 *
 * `documentVersionId` is the `versionId` the kiosk was served on the DOCUMENT_SIGNATURE
 * screen's `agreement` sibling (`resolvedAgreementSchema`, check-in-flow.ts:997) — naming the
 * VERSION, not the document, is what makes "which text did they actually read" answerable.
 *
 * BOTH `typedName` AND `signatureFileId` ARE OPTIONAL — REVERSING S15 Constraint 2 ("typed
 * name only"). `signatureFileId` is the `StoredFile.id` a kiosk got back from
 * `POST /api/device/v1/agreement-signatures`, uploaded before this `POST /visits` call, the
 * same "upload first, reference by id" shape `POST /visit-photos` already uses.
 *
 * NEITHER BEING PRESENT IS ALLOWED AT THIS LAYER, DELIBERATELY: whether a screen actually
 * REQUIRES a typed name and/or a drawn signature is a property of the screen config
 * (`documentSignatureScreenConfigSchema.requiresTypedName`/`requiresDrawnSignature` in
 * ./check-in-flow.js, enforced there by its own `.superRefine`), not of this wire shape. This
 * schema is the acceptance's WIRE FORMAT for every screen configuration at once; pinning "at
 * least one required" here would make it impossible to represent a legitimately-permissive
 * screen. The service layer is where a submitted acceptance is checked against the screen it
 * was actually collected for.
 *
 * NOT `.strict()`, deliberately: a future kiosk build that sends an extra key must not be
 * 400'd by a server that predates it (S15 Constraint 3).
 */
export const agreementAcceptanceInputSchema = z.object({
    documentVersionId: z.string().min(1).max(64),
    typedName: z.string().trim().min(1).max(ACCEPTANCE_TYPED_NAME_MAX_CHARS).optional(),
    signatureFileId: z.string().min(1).optional(),
});
/**
 * `POST /api/device/v1/visits` — a kiosk checking a walk-up visitor in.
 *
 * `visitorEmail` is REQUIRED, unlike `personDTOSchema`'s optional email:
 * `docs/adr/0010` requires a `VisitorProfile` to carry a stable natural key
 * today, because that is what makes cross-tenant identity linking possible
 * later without a migration. It is also the dedupe key — a second visit from
 * the same email in the same tenant reuses the existing `VisitorProfile` row
 * rather than creating a duplicate.
 *
 * `hostPersonId` is a `Person.id` (`cuid(2)`, like every id in this package),
 * not an RFC-4122 UUID, so `.min(1)` — not `.uuid()`.
 *
 * `idempotencyKey` IS a real UUID: the kiosk mints a fresh one client-side
 * per check-in attempt (not looked up against any stored row), and the
 * server treats a repeated key as "this is the same submit, not a second
 * visit" — the mechanism that makes a double-tap on the confirm button
 * produce one `Visit`, not two (PRD-004 CHK-08).
 *
 * `answers` is OPTIONAL AND DEFAULTED TO `[]`, and that is load-bearing, not a
 * convenience default: this is a published cross-team device contract a Flutter
 * kiosk fleet is already shipping against, submitting no `answers` key at all, and
 * back-compat means that submission must keep checking a visitor in unchanged
 * forever — never start failing the day this field was added. Capped at 50 and
 * deduplicated by `fieldKey`: a flow's `FIELD_COLLECTION` screens cap placements at
 * 50 each (`createCheckInScreenSchema`), and two answers for the same question in one
 * submission have no defined precedence.
 */
export const createVisitSchema = z.object({
    visitorFirstName: personName({ max: 100 }),
    visitorLastName: personName({ max: 100 }),
    visitorEmail: z.string().trim().email().max(254),
    visitorCompany: label({ max: 120 }).nullable().default(null),
    hostPersonId: z.string().min(1),
    purpose: freeText({ max: 280 }).nullable().default(null),
    idempotencyKey: z.string().uuid(),
    answers: z
        .array(checkInAnswerSchema)
        .max(50)
        .default([])
        .superRefine((answers, ctx) => {
        const seen = new Set();
        answers.forEach((answer, index) => {
            if (seen.has(answer.fieldKey)) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    path: [index, 'fieldKey'],
                    message: 'DUPLICATE_ANSWER_FIELD',
                });
            }
            seen.add(answer.fieldKey);
        });
    }),
    /**
     * An arrival code from a `VisitorInvite`, when this check-in is redeeming one (INV-09).
     *
     * OPTIONAL, AND THAT IS THE WHOLE POINT. This is a published cross-team device contract
     * a Flutter kiosk fleet already ships against; a body that has never heard of invites must
     * keep checking a visitor in byte-for-byte as it did before S11 (roadmap "the native kiosk
     * is a separate track", milestone Constraint 5). No existing member becomes optional, no
     * existing member is removed.
     *
     * DELIBERATELY LOOSE — `.min(1).max(32)`, NOT `INVITE_CODE_PATTERN`. A visitor types this
     * off a phone: lowercase, with a hyphen, with a space, with `I` for `1`. The server
     * normalises it (`normalizeInviteCode`) and only then checks the pattern, so a mistyped
     * code returns the SAME generic `INVITE_CODE_INVALID` as a wrong one. Enforcing the
     * pattern here would answer a malformed code with `400 VALIDATION_FAILED` and a
     * well-formed wrong one with `404 INVITE_CODE_INVALID` — a two-outcome oracle that tells
     * a guesser which of their attempts was shaped like a real code (milestone Constraint 7,
     * docs/008 S8).
     *
     * When present and valid, the invite supplies the visitor's identity and host and the
     * server IGNORES this body's `visitorFirstName`/`visitorLastName`/`visitorEmail`/
     * `hostPersonId` (S11-TASK-009). It never supplies `answers`: a required signature or
     * agreement screen is still the visitor's to complete (INV-10, Constraint 6).
     */
    inviteCode: z.string().trim().min(1).max(32).optional(),
    /**
     * The `fileId` returned by `POST /api/device/v1/visit-photos` for the photograph this
     * visitor just had taken (PRD-004 CHK-21).
     *
     * OPTIONAL, AND THAT IS THE WHOLE POINT — the same reasoning as `inviteCode` above. This
     * is a published cross-team device contract a Flutter kiosk fleet already ships against,
     * and today that fleet SKIPS `PHOTO_CAPTURE` screens entirely
     * (`apps/kiosk/lib/features/check-in/screen-flow.dart`). A body that has never heard of
     * photos must keep checking a visitor in byte-for-byte as it did before S13, forever. No
     * existing member becomes optional, no existing member is removed, and the absence of this
     * field is never an error — not even when the flow's photo screen is marked required
     * (S13 milestone Constraint 3; CHK-24 is satisfied by the same rule).
     *
     * AN OPAQUE SERVER-MINTED ID, NOT A PATH AND NOT A URL. The server resolves it to a
     * `StoredFile` row scoped to the token's tenant and uses THAT row's `storageKey`; nothing
     * a kiosk sends ever becomes part of an object key (docs/008 S11).
     */
    photoFileId: z.string().min(1).max(64).optional(),
    /**
     * The visitor type the visitor chose on the VISITOR_TYPE_SELECTION screen (S24).
     *
     * OPTIONAL for the same reason as `inviteCode`, `photoFileId` and `agreementAcceptances`:
     * a fleet build that predates the choice keeps checking visitors in unchanged. When
     * present it must name a type of this tenant that is offered at the kiosk's location;
     * the server derives `flowId` from it (the flow the kiosk actually played is the one
     * `(location, type)` resolves to), and a kiosk never sends a `flowId` of its own.
     */
    visitorTypeId: z.string().min(1).max(64).optional(),
    /**
     * The agreements this visitor accepted on the DOCUMENT_SIGNATURE screens of the flow they
     * were served (PRD-008 DOC-03/DOC-04).
     *
     * OPTIONAL AND DEFAULTED, AND THAT IS THE WHOLE POINT — the third instance of the exact
     * pattern `inviteCode` and `photoFileId` above document at length. This is a published
     * cross-team device contract a Flutter kiosk fleet already ships against, and today that
     * fleet SKIPS `DOCUMENT_SIGNATURE` screens entirely
     * (`apps/kiosk/lib/features/check-in/screen-flow.dart`). A body that has never heard of
     * agreements must keep checking a visitor in byte-for-byte as it did before S15, forever.
     * No existing member becomes optional, no existing member is removed, no existing member's
     * meaning changes.
     *
     * ABSENCE IS NEVER AN ERROR — not even when the flow's agreement screen is marked required
     * (S15 Constraint 4, inherited verbatim from S13). DOC-03's "must sign before the flow
     * proceeds" is a CLIENT obligation; enforcing it here would 400 every kiosk in the field on
     * the deploy that shipped it. What the server does enforce is that an acceptance it IS given
     * is well-formed, current and belongs to this tenant (S15-TASK-013).
     *
     * Deduplicated by `documentVersionId` below; the service additionally refuses two distinct
     * versions of the SAME document, which this schema cannot see.
     */
    agreementAcceptances: z
        .array(agreementAcceptanceInputSchema)
        .max(MAX_AGREEMENT_ACCEPTANCES_PER_VISIT)
        .default([])
        .superRefine((entries, ctx) => {
        const seen = new Set();
        entries.forEach((entry, index) => {
            if (seen.has(entry.documentVersionId)) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    path: [index, 'documentVersionId'],
                    message: 'DUPLICATE_DOCUMENT_VERSION',
                });
            }
            seen.add(entry.documentVersionId);
        });
    }),
});
/**
 * `GET /api/device/v1/visits/hosts?q=` — a kiosk searching the directory for a
 * host. It is nested under `/visits` because it exists only to fill in a
 * check-in's `hostPersonId`; that is the registered path (see
 * `visitor-surface-routes.ts`) and it is what a `HOST_LOOKUP` field in
 * ./check-in-flow.ts renders a search against.
 *
 * The `min(2)` floor is deliberate, not a UX nicety: a kiosk is an
 * unauthenticated-ish device principal sitting in a public lobby, and a
 * single-character query would let anyone at the tablet enumerate the whole
 * directory one letter at a time (PRD-004 CHK-06). Two characters is short
 * enough to still feel instant while cutting that off.
 */
export const hostSearchQuerySchema = z.object({
    q: z.string().trim().min(2).max(80),
});
/**
 * `POST /api/device/v1/visits/check-out/lookup` — the body a kiosk sends when a visitor
 * types their own name to find their visit and check themselves out (PRD-004 CHK-16,
 * docs/plans/kiosk-self-check-out.md "Decisions").
 *
 * PII IN A BODY, NEVER A QUERY STRING — the same S12 reasoning `hostSearchQuerySchema`
 * immediately above and `lookupInviteSchema` state: a name in a request target lands in the
 * access log, any proxy in front of the API, CloudWatch and a `Referer` header.
 *
 * `min(2)` is the same enumeration floor as `hostSearchQuerySchema`, for the same reason: a
 * kiosk sits in a public lobby, and a one-character query must not let anyone at the tablet
 * walk the whole day's roster one letter at a time. The service applies a stricter WHOLE-WORD
 * matching rule on top of this floor (`matchesSelfCheckOutName`) — this schema only bounds
 * the length.
 */
export const selfCheckOutLookupSchema = z.object({
    name: z.string().trim().min(2).max(120),
});
/**
 * One masked match `POST /api/device/v1/visits/check-out/lookup` returns.
 *
 * MASKED ON PURPOSE (PRD-004 CHK-13): `lastInitial`, never `lastName` — a result must reveal
 * nothing the person who typed the query did not already know, apart from when they arrived.
 * No host, no company, no purpose, no photo. `lastInitial` is `''` for a visitor whose last
 * name is empty, never a fabricated placeholder.
 */
export const selfCheckOutCandidateDTOSchema = z.object({
    visitId: z.string().min(1),
    firstName: z.string().min(1),
    lastInitial: z.string().max(1),
    checkedInAt: z.string().datetime(),
});
/**
 * `POST /api/device/v1/visits/:id/check-out` — the confirmation body once a visitor has
 * picked their match. Deliberately narrower than `visitDTOSchema`: this is the goodbye
 * screen's data, not the admin visit record, and there is no reason for a kiosk that just
 * checked a stranger's name out to also learn their host, company or purpose.
 */
export const selfCheckOutResultDTOSchema = z.object({
    visitId: z.string().min(1),
    firstName: z.string().min(1),
    checkedOutAt: z.string().datetime(),
});
/**
 * The FILTER half of the admin visitor log's query, with no pagination — the one
 * schema `GET /api/web/visits` and (S10 Phase 2) the CSV export both build on, so
 * the file a front desk downloads cannot describe a different set of rows than the
 * screen it was taken from.
 *
 * A PLAIN `z.object`, deliberately: `.merge()` below only works on a ZodObject, and
 * a cross-field `.superRefine` (e.g. `from < to`) would turn this into a ZodEffects
 * and break that composition. A reversed or empty range is not an error — it is an
 * empty half-open interval that returns zero rows and the page's filtered-empty
 * state, which is honest and needs no error code.
 *
 * `from`/`to` are ISO 8601 INSTANTS (never a calendar day), filtered half-open
 * against `checkedInAt`: `>= from`, `< to`. A browser converts the operator's chosen
 * days into instants with its own timezone; location-local day boundaries are
 * deliberately deferred (see the milestone's Technical Notes), because a screen that
 * mixed the two would make "today" mean two different things in one table.
 *
 * `q` is ONE bounded term matched against the visitor's and the host's name. Separate
 * `visitorName`/`hostName` parameters would double the filter surface for a front desk
 * that types one box. The `min(2)` floor mirrors `hostSearchQuerySchema`'s.
 */
export const visitFilterQuerySchema = z.object({
    status: visitStatusSchema.optional(),
    locationId: z.string().min(1).optional(),
    from: z.string().datetime({ offset: true }).optional(),
    to: z.string().datetime({ offset: true }).optional(),
    q: z.string().trim().min(2).max(100).optional(),
});
/** `GET /api/web/visits` — the admin visitor log: the shared filter, plus pagination. */
export const visitListQuerySchema = pageQuerySchema.merge(visitFilterQuerySchema);
//# sourceMappingURL=visit.js.map